/* eslint-disable react/display-name */
import React, { useEffect, useState, forwardRef } from 'react';
import { NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { ListItem, ListItemButton, Collapse, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

const CustomRouterLink = forwardRef((props, ref) => (
  <NavLink
    ref={ref}
    {...props}
    role={undefined}
  />
));

const buttonStyle = {
  color: 'text.primary',
  padding: '10px 8px',
  justifyContent: 'flex-start',
  textTransform: 'none',
  letterSpacing: 0,
  borderRadius: 1,
  width: '100%',
  '&.Mui-selected': {
    backgroundColor: 'action.selected',
    color: 'primary.main',
    fontWeight: 'typography.fontWeightMedium',
    '& .MuiSvgIcon-root': {
      color: 'primary.main',
    },
  },
  '&.Mui-selected:hover': {
    backgroundColor: 'action.hover',
  },
};
const expandIconStyle = {
  marginLeft: 'auto',
  height: 16,
  width: 16
};

const NavigationListItem = (props) => {
  const {
    title,
    href,
    depth,
    children,
    active,
    end,
    icon: Icon,
    styles,
    open: openProp,
    brief,
    label: Label,
    ...rest
  } = props;

  const StyledIcon = styled(Icon)(() => ({
    color: 'inherit',
    display: 'flex',
    alignItems: 'center',
    marginRight: brief ? 0 : 6,
  }));

  const [open, setOpen] = useState(openProp);

  useEffect(() => {
    setOpen(openProp);
  }, [openProp]);

  let paddingLeft = 8;
  if (depth > 0) {
    paddingLeft = 32 + 8 * depth;
  }

  if (children && !brief) {
    return (
      <ListItem
        {...rest}
        sx={{
          display: 'block',
          paddingY: 0,
          ...styles
        }}
        disableGutters
      >
        <ListItemButton
          selected={active}
          sx={buttonStyle}
          onClick={() => setOpen(open => !open)}
          style={{paddingLeft}}
        >
          <StyledIcon />
          {title}
          {open ? (
            <ExpandLessIcon
              sx={expandIconStyle}
              color="inherit"
            />
          ) : (
            <ExpandMoreIcon
              sx={expandIconStyle}
              color="inherit"
            />
          )}
        </ListItemButton>
        <Collapse in={open}>{children}</Collapse>
      </ListItem>
    );
  } else {
    return (
      <ListItem
        {...rest}
        sx={{
          display: 'flex',
          paddingY: 0,
          ...styles
        }}
        disableGutters
      >
        <ListItemButton
          className={`depth-${depth}`}
          selected={active}
          sx={{
            ...buttonStyle,
            justifyContent: brief ? 'center' : 'flex-start',
            padding: brief ? '10px 0' : buttonStyle.padding,
            fontWeight: 'typography.fontWeightRegular',
            '&.depth-0': {
              fontWeight: 'typography.fontWeightMedium'
            },
            '&.active': {
              color: 'primary.main',
              fontWeight: 'typography.fontWeightMedium',
              '& .MuiSvgIcon-root': {
                color: 'primary.main'
              }
            }
          }}
          component={CustomRouterLink}
          style={{paddingLeft: brief ? 0 : paddingLeft, minWidth: 'auto'}}
          to={href}
          end={end}
          aria-label={brief ? title : undefined}
        >
          {brief ? (
            <Tooltip title={title} placement="right">
              <StyledIcon />
            </Tooltip>
          ) : (
            <>
              <StyledIcon />
              <span>{title}</span>
            </>
          )}
          {Label && !brief && (
            <span style={{display: 'flex', alignItems: 'center', marginLeft: 'auto'}}>
              <Label />
            </span>
          )}
        </ListItemButton>
      </ListItem>
    );
  }
};

NavigationListItem.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node,
  end: PropTypes.bool,
  styles: PropTypes.object,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  icon: PropTypes.any.isRequired,
  label: PropTypes.any,
  open: PropTypes.bool,
  brief: PropTypes.bool,
  title: PropTypes.string.isRequired
};

NavigationListItem.defaultProps = {
  active: false,
  depth: 0,
  end: true,
  open: false
};

export default NavigationListItem;
